<template>
  <div class='reports-layout'>
    <div class="filters-section">
      <div class="filters-section__filter-and-search-area">
        <div class='filters-section__filters'>
        <advanced-filters-header
          layout='reports'
          @applyDomains='() => {}'
          @applyOrganizations='() => {}'
        />
        <div class="date-select">
          <custom-datepicker
            v-model="date"
            type="daterange"
            range-separator="To"
            start-placeholder="Start day"
            end-placeholder="End day"
            :title="$t('dashboard.header.select.date.outside').toString()"
            class='custom-date-picker'
            format="DD MMM YYYY"
          />
        </div>
      </div>
        <div class='filters-section__search'>
        <v-text-field
          @keyup.enter='submitSearchFilter'
          @keydown.native.escape="clearSearch"
          label="Search"
          solo
          v-model="search"
        >
          <template v-slot:append>
            <div @mousedown.prevent="search ? clearSearch() : null" style="cursor:pointer; display:flex; align-items:center;">
              <v-icon :size="search ? 16 : 24" :color="search ? 'var(--brand-primary)' : ''">{{ search ? 'close' : 'search' }}</v-icon>
            </div>
          </template>
          <template v-slot:append-outer>
            <div v-if='search' @mouseenter='searchMouseEnter' @click='submitSearchFilter' class='search-animation-wrapper'>
              <lottie-animation
                class='search-animation'
                :loop='true'
                :animationData="require('@/assets/icons/animations/search.json')"
                ref="searchAnimation"
                @loopComplete="loopComplete"
              />
            </div>
          </template>
        </v-text-field>
      </div>
      </div>
      <v-divider class='filters-section__divider'
                 inset
                 vertical
      />
      <div class='filters-section__domains_section'>
        <v-btn class='generate-report-button' color="primary" @click='generateReport' :disabled="disableGenerateReport">
          {{$t('report.generate.popup.title')}}
        </v-btn>
        <div class='filters-section__domains_section__label'>{{ $t('risk.score.selected.domains') }}</div>
        <div class='filters-section__domains_section__list'>{{ domainSelected.join(', ') }}</div>
      </div>
    </div>
    <div class='reports-layout__header'>
      <div class='reports-layout__header__title'>
        {{ $t('reports.layout.title') }}
      </div>
    </div>
    <div class='counter'> {{getCounter}} </div>
    <div class='reports-layout__content' :class='{ loading: highlightReportsLoading }'>
      <div class='reports-layout__content__table-container'>
        <div class='reports-layout__content__table-container__table'>
          <wombat-table
            :items="reportsList"
            :columns="tableColumns"
            :resize="false"
            :loadingItems="true"
            :columnsReorder="false"
            :itemHeight="58"
            :noResultsMessage="$t('noResultsFound')"
            @columnSorted="columnSorted"
          >
            <component
              slot="row-cell"
              slot-scope="rowCell"
              class="row-cell"
              :is="tableRowCellComponentsHash[rowCell.column.name] || tableRowCellComponentsHash.default"
              :item="rowCell.item"
              :column="rowCell.column"
            />
          </wombat-table>
        </div>
        <div class='reports-layout__content__table-container__stats'>
          <simple-stats-widget
            :labels='labels'
            :values='values' />
        </div>
      </div>
      <div class="reports-layout__content__footer">
        <advanced-pagination
          tab='reports'
          :pages-amount='pages'
          :is-show-pagination='isShowPagination'
          @changedSelectedPage='changedSelectedPage'
          @changedItemsAmountPerPage='changedItemsAmountPerPage'
        />
      </div>
      <page-loading-overlay :show="highlightReportsLoading && reportsList.length === 0" contained />
    </div>
    <YesNoPopup
      :visible-popup="showGenerateReportInitiated"
      :title="'report.generate.popup.title'"
      :message="'report.generate.popup.message'"
      :no-caption="'confirm.cancel'"
      :yes-caption="'confirm.ok'"
      :loading="loadingForGenerateReportPopup"
      @clicked='confirmGenerateReportPopupClicked'
    />
    <error-message :errorText="reportError" @close="reportError = ''" />
  </div>
</template>

<script>
import WombatTable from '@/components/WombatTable/WombatTable.vue';
import WombatRowDefaultCell from '@/components/WombatTable/WombatRowDefaultCell.vue';
import DateTableCell from '@/components/tableCells/centralizedIssuesTableCells/DateTableCell.vue';
import TldTableCell from '@/components/tableCells/centralizedIssuesTableCells/TldTableCell.vue';
import ReportSourceTableCell from '@/components/tableCells/reportsTableCells/ReportSourceTableCell.vue';
import ReportIconTableCell from '@/components/tableCells/reportsTableCells/ReportIconTableCell.vue';
import ReportTypeTableCell from '@/components/tableCells/reportsTableCells/ReportTypeTableCell.vue';
import {CHANGE_HIGHLIGHT_REPORTS_LIST_OPTIONS, GENERATE_REPORT} from '@/store/reports/actionTypes';
import { mapGetters } from 'vuex';
import { getEmptyColumn, reportsColumns } from '@/services/tableColumns';
import YesNoPopup from '@/components/popup/YesNoPopup.vue';
import CustomDatepicker from '@/components/datepicker/CustomDatepicker.vue';
import AdvancedPagination from '@/components/common/AdvancedPagination.vue';
import { SET_REPORTS_DATE_FILTER } from '@/store/filters/actionTypes';
import {CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS, LOAD_DOMAINS_FROM_CONFIGURATION} from '@/store/project/actionTypes';
import { LOAD_DOMAINS } from '@/store/user/actionTypes';
import LottieAnimation from 'lottie-web-vue';
import { CLEAR_HIGHLIGHT_REPORTS_PAGINATION_STATE, UPDATE_ITEMS_PER_PAGE } from '@/store/pagination/actionTypes';
import PageLoadingOverlay from '@/components/common/PageLoadingOverlay.vue';
import ErrorMessage from '@/components/common/ErrorMessage.vue';
import DonutWidget from '@/components/Dashboard/DonutWidget.vue';
import SimpleStatsWidget from '@/components/reports/SimpleStatsWidget.vue';
import AdvancedFiltersHeader from '@/components/common/AdvancedFiltersHeader.vue';
import {activityType} from '@/constants/activity';
import {LOAD_ACTIVITIES} from '@/store/activity/actionTypes';

export default {
  name: 'ReportsLayout',
  components: {
    AdvancedFiltersHeader,
    SimpleStatsWidget,
    DonutWidget,
    WombatTable,
    YesNoPopup,
    CustomDatepicker,
    LottieAnimation,
    AdvancedPagination,
    PageLoadingOverlay,
    ErrorMessage,
  },
  data() {
    return {
      search: '',
      showGenerateReportInitiated: false,
      loadingForGenerateReportPopup: false,
      locallyGeneratedDomains: [],
      reportError: '',
    };
  },
  watch: {
    // Reload whenever the effective domain set changes. Domain and organization
    // filters both funnel through the domain filter store (selecting an
    // organization commits its domains via SET_ORGANIZATION_FILTER), so watching
    // it here keeps the list interactive for every filter, not just the date.
    async domainFilter() {
      await this.apply();
    },
    async profile() {
      if (this.profile) {
        await this.$store.dispatch(LOAD_DOMAINS);
        await this.$store.dispatch(LOAD_DOMAINS_FROM_CONFIGURATION);
        await this.$store.dispatch(CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS, {
          noLimit: true,
        });
      }
    },
    async date() {
      await this.apply();
    },
  },
  methods: {
    async confirmGenerateReportPopupClicked(value) {
      if (value) {
        this.loadingForGenerateReportPopup = true;
        try {
          const domains = this.domainToPass.filter(domain => !this.runningReportDomains.includes(domain));
          await this.$store.dispatch(GENERATE_REPORT, {
            tld: domains,
            companyId: this.profile.company.id,
          });
          await this.$store.dispatch(LOAD_ACTIVITIES, {type: activityType.GENERATE_REPORT});
          this.locallyGeneratedDomains = [...new Set([...this.locallyGeneratedDomains, ...domains])];
        } catch (e) {
          this.reportError = e.message || e;
        } finally {
          this.loadingForGenerateReportPopup = false;
        }
      }
      this.showGenerateReportInitiated = false;
    },
    async generateReport() {
      this.showGenerateReportInitiated = true;
    },
    async apply() {
      if (this.domainSelected.length > 0) {
        await this.$store.dispatch(CHANGE_HIGHLIGHT_REPORTS_LIST_OPTIONS, {
          page: 1,
          limit: this.itemsPerPage,
          turnOnLoading: true,
          domain: this.domainToPass,
          timeRange: [this.startDate, this.endDate],
        });
      }
    },
    async clearSearch() {
      this.search = '';
      await this.$store.dispatch(CHANGE_HIGHLIGHT_REPORTS_LIST_OPTIONS, {
        search: this.search,
        page: 1,
        searchUpdated: true,
      });
    },
    async submitSearchFilter() {
      if (this.search.length) {
        await this.$store.dispatch(CHANGE_HIGHLIGHT_REPORTS_LIST_OPTIONS, {
          search: this.search,
          searchUpdated: false,
          page: 1,
        });
      }
    },
    async columnSorted({ column, direction }) {
      await this.$store.dispatch(CHANGE_HIGHLIGHT_REPORTS_LIST_OPTIONS, {
        page: this.page,
        limit: this.itemsPerPage,
        search: this.search,
        sort: direction
          ? {
            [column.sortingField || column.field]: direction,
          }
          : {},
        domain: this.domainToPass,
      });
    },
    searchMouseEnter() {
      this.$refs.searchAnimation.play();
    },
    loopComplete() {
      this.$refs.searchAnimation.pause();
    },
    async changedSelectedPage(currentPage) {
      window.scrollTo(0, 0);
      this.page = currentPage;
      await this.$store.dispatch(CHANGE_HIGHLIGHT_REPORTS_LIST_OPTIONS, {
        page: this.page,
        domain: this.domainToPass,
        turnOnLoading: true,
      });
    },
    async changedItemsAmountPerPage(itemsPerPage) {
      window.scrollTo(0, 0);
      this.page = 1;
      await this.$store.dispatch(UPDATE_ITEMS_PER_PAGE, { itemsPerPage, tab: 'reports' });
      await this.$store.dispatch(CHANGE_HIGHLIGHT_REPORTS_LIST_OPTIONS, {
        domain: this.domainToPass,
        limit: itemsPerPage,
        page: this.page,
        turnOnLoading: true,
      });
    },
  },
  async created() {
    if (this.profile) {
      await this.$store.dispatch(LOAD_DOMAINS);
      await this.$store.dispatch(LOAD_DOMAINS_FROM_CONFIGURATION);
      await this.$store.dispatch(CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS, {
        noLimit: true,
      });
    }
    const yearBack = new Date(this.endDate);
    yearBack.setFullYear(yearBack.getFullYear() - 1);
    this.date = [yearBack, this.endDate];
    await this.$store.dispatch(CLEAR_HIGHLIGHT_REPORTS_PAGINATION_STATE);
    await this.$store.dispatch(CHANGE_HIGHLIGHT_REPORTS_LIST_OPTIONS, {
      page: 1,
      limit: this.itemsPerPage,
      turnOnLoading: true,
      search: '',
      domain: this.domainToPass,
      timeRange: this.date,
      sort: {
        date: 'asc',
      },
    });
    await this.$store.dispatch(LOAD_ACTIVITIES, {type: activityType.GENERATE_REPORT});
  },
  computed: {
    ...mapGetters([
      'highlightReports',
      'highlightReportsStats',
      'domainFilter',
      'highlightReportsListSort',
      'domains',
      'profile',
      'configurationDomains',
      'reportsDateFilter',
      'highlightReportsPaginationState',
      'paginationItemsPerPageByTabs',
      'highlightReportsLoading',
      'organizationFilter',
      'configurationCloudAccounts',
      'reportActivities',
    ]),
    domainSelected: {
      get() {
        return this.domainFilter;
      },
    },
    disableGenerateReport() {
      if (this.domainSelected.length === 0) {
        return true;
      }
      const hasAvailableDomain = this.domainSelected.some(
        domain => !this.reportActivities.some(activity => activity.tld === domain) &&
          !this.locallyGeneratedDomains.includes(domain),
      );
      return !hasAvailableDomain;
    },
    runningReportDomains() {
      return [...new Set([
        ...this.reportActivities.map(item => item.tld),
        ...this.locallyGeneratedDomains,
      ])];
    },
    listOfCloudAccounts() {
      return this.configurationCloudAccounts
        .filter(item => this.organizationFilter.includes(item.organization.name)).map(item => item.configData);
    },
    labels() {
      return [
        this.$t('report.label.highlights'),
        this.$t('report.label.cyber.risk'),
        this.$t('report.label.va'),
        this.$t('report.label.cloud.compliance'),
      ];
    },
    values() {
      let highlights = 0;
      let cyberRisk = 0;
      let va = 0;
      let cloudComplinace = 0;
      Object.keys(this.highlightReportsStats).forEach(domain => {
        const stat = this.highlightReportsStats[domain];
        highlights += stat.discovery;
        cyberRisk += stat.cyberRisk;
        va += stat.vulnerabilityAssessment;
        cloudComplinace += stat.cloudCompliance;
      });
      return [highlights, cyberRisk, va, cloudComplinace];
    },
    listOfDomains() {
      const domainsSet = new Set(this.domains);
      this.configurationDomains.forEach(configurationDomain => domainsSet.add(configurationDomain.configData));
      return Array.from(domainsSet);
    },
    domainToPass() {
      return this.domainSelected.length > 0 ? this.domainSelected : this.domains;
    },
    date: {
      get() {
        return this.reportsDateFilter;
      },
      async set(value) {
        await this.$store.dispatch(SET_REPORTS_DATE_FILTER, value);
      },
    },
    startDate() {
      if (this.date !== '' && this.date) {
        return this.date[0];
      }
      return new Date();
    },
    endDate() {
      if (this.date !== '' && this.date) {
        return this.date[1];
      }
      return new Date();
    },
    itemsPerPage() {
      return this.paginationItemsPerPageByTabs.reports;
    },
    pages() {
      return this.highlightReportsPaginationState?.pageCount || Math.ceil(this.reportsList.length / this.itemsPerPage);
    },
    isShowPagination() {
      return this.pages > 1;
    },
    reportsList() {
      return this.highlightReports;
    },
    tableColumns() {
      const columns = [
        reportsColumns.tld,
        reportsColumns.date,
        reportsColumns.type,
        reportsColumns.source,
        reportsColumns.icon,
      ]
        .filter(column => column)
        .map(column => ({
          ...column,
          title: column.title && this.$t(column.title),
          sort: this.highlightReportsListSort[column.sortingField || column.field],
        }));

      return [getEmptyColumn(32), ...columns, getEmptyColumn(32)];
    },
    tableRowCellComponentsHash() {
      return {
        tld: TldTableCell,
        date: DateTableCell,
        type: ReportTypeTableCell,
        source: ReportSourceTableCell,
        icon: ReportIconTableCell,
        default: WombatRowDefaultCell,
      };
    },
    getCounter() {
      let from = ((this.highlightReportsPaginationState.page - 1) * this.highlightReportsPaginationState.count) + 1;
      let to = this.highlightReportsPaginationState.page * this.highlightReportsPaginationState.count;
      if (Number(this.highlightReportsPaginationState.page) === this.highlightReportsPaginationState.pageCount) {
        from = (this.highlightReportsPaginationState.total - this.highlightReportsPaginationState.count) + 1;
        to = this.highlightReportsPaginationState.total;
      }
      if (to === 0) {
        from = 0;
      }
      return `${from} to ${to} of ${this.highlightReportsPaginationState.total}`;
    },
  },
};
</script>

<style lang='scss'>
@import '~@/assets/styles/design-system';

.date-select {
  .custom-date-picker {
    .datepicker.datepicker {
      background-color: var(--bg-primary);
      border: $border-width-thin solid var(--border-primary);
      &-icon {
        margin-right: $space-3;
        color: var(--text-secondary);
      }
    }
  }
}
.filters-section {
  &__search {
    .search-animation-wrapper {
      margin-top: -$space-1;
      &:hover {
        cursor: pointer;
      }
    }
    .search-animation {
      height: rem(30);
      filter: brightness(0) saturate(100%);
    }
    [data-theme="dark"] & .search-animation {
      filter: brightness(0) saturate(100%) invert(60%);
    }
    .v-text-field.v-text-field--solo:not(.v-text-field--solo-flat) > .v-input__control > .v-input__slot {
      box-shadow: none;
      border: $border-width-thin solid var(--border-primary);
      border-radius: $radius-sm;
    }
    .v-input__icon.v-input__icon--append.v-input__icon--append {
      margin-top: 0;
    }
    .v-text-field.v-text-field--solo .v-input__control {
      min-height: $input-height-md;
    }
  }
}
.reports-layout {
  .wombat-table {
    --displayactioncell: none;
    padding: $space-4 0;
    .wombat-header {
      padding-bottom: $space-2;
    }
    .wombat-row.wombat-row {
      border-top: none;
      border-left: none;
      border-right: none;
    }
    .wombat-overlay {
      &:hover {
        background-color: var(--bg-tertiary);
        --display: block;
        --bg: linear-gradient(90deg, rgba(0,0,0,0) 0%, var(--bg-tertiary) 100%);
        --displayactioncell: flex;
      }
    }
  }
}
</style>
<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

:deep(.date-select) {
  margin-top: $space-0-5;

  .custom-date-picker {
    .datepicker {
      background-color: var(--bg-primary);
      border: $border-width-thin solid var(--border-primary);
      &-icon {
        margin-right: $space-3;
        color: var(--text-secondary);
      }
    }
  }
}

.filters-section {
  display: flex;
  flex-direction: row;
  margin-top: -$space-3;

  &__search {
    width: rem(665);
    margin-top: $space-4;

    :deep() {
      .v-input__icon--append {
        .v-icon {
          font-size: rem(24);
          color: var(--text-secondary);
        }
      }
      .search-animation-wrapper {
        margin-top: -$space-1;
        &:hover {
          cursor: pointer;
        }
      }
      .search-animation {
        height: rem(30);
      }
      .v-text-field.v-text-field--solo:not(.v-text-field--solo-flat) > .v-input__control > .v-input__slot {
        box-shadow: none;
        border: $border-width-thin solid var(--border-primary);
        border-radius: $radius-sm;
      }
      .v-input__icon.v-input__icon--append {
        margin-top: 0;
      }
      .v-text-field.v-text-field--solo .v-input__control {
        min-height: $input-height-md;
      }
    }
  }

  &__filter-and-search-area {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  &__filters {
    min-width: rem(700);
    align-items: center;
    display: flex;
    flex-direction: row;
    margin-top: -$space-8;
    gap: $space-6;
  }

  &__divider {
    margin-top: $space-0-5;
    min-height: rem(115);
    margin-right: $space-1;
  }

  &__domains_section {
    margin-left: $space-6;
    max-height: rem(100);
    max-width: 96%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    margin-top: rem(120);

    .generate-report-button.v-btn.v-btn.v-btn {
      display: inline-block;
      cursor: pointer;
      height: $btn-height-md;
      width: rem(230);
      border-radius: $radius-sm;
      background-color: var(--brand-primary);
      color: #ffffff;
      font-size: $font-size-base;
      font-family: $font-family-medium;
      letter-spacing: $letter-spacing-wide;
      line-height: $line-height-tight;
      text-align: center;
      text-transform: uppercase;
      margin-bottom: $space-4;
      margin-top: rem(-100);
    }

    &__label {
      color: var(--text-secondary);
      font-family: $font-family-regular;
      letter-spacing: $letter-spacing-wide;
      line-height: $line-height-snug;
      text-transform: uppercase;
      font-size: $font-size-sm;
      margin-top: $space-4;
      padding-bottom: $space-1;
    }

    &__list {
      color: var(--text-primary);
      font-family: $font-family-medium;
      font-size: $font-size-base;
      text-transform: uppercase;
      overflow-wrap: break-word;
      overflow: auto;
      height: rem(45);
      margin-right: $space-8;
      width: 94%;
    }
  }
}

.reports-layout {
  padding: $space-8;

  .counter {
    margin-left: $space-0-5;
    margin-bottom: $space-4;
    font-family: $font-family-regular;
    font-size: $font-size-sm;
    text-transform: uppercase;
    color: var(--text-secondary);
  }

  &__header {
    display: flex;
    &__title {
      margin-bottom: $space-5;
      color: var(--text-primary);
      font-family: $font-family-bold;
      font-size: $font-size-2xl;
      font-weight: bold;
      line-height: $line-height-tight;
      letter-spacing: $letter-spacing-normal;
    }
  }

  &__content {
    position: relative; // Required for contained loading overlay
    min-height: calc(100vh - 280px); // Account for header area

    &__footer {
      margin-top: $space-6;
      margin-bottom: $space-12;
    }
    &__table-container {
      display: flex;
      flex-direction: row;

      &__table {
        flex-basis: 100%;
      }
    }

    :deep(.wombat-table) {
      --displayactioncell: none;
      padding: $space-4 0;
      max-width: 65%;

      .wombat-header {
        padding-bottom: $space-2;
      }

      .cell {
        z-index: $z-base + 10;
      }

      .wombat-row.wombat-row {
        border-top: none;
        border-left: none;
        border-right: none;
      }

      .wombat-overlay {
        &:hover {
          background-color: var(--bg-tertiary);
          --display: block;
          --bg: linear-gradient(90deg, rgba(0,0,0,0) 0%, var(--bg-tertiary) 100%);
          --displayactioncell: flex;
        }
      }
    }
  }
}
</style>
